/**
 * startHere.ts — W6-DailyHardening / W7-CadenceTodoFeed
 *
 * Computes the single "Start here" anchor sentence for AeDaily and BdrDaily.
 *
 * W7 change: computeStartHereBdr now accepts an optional cadenceQueue
 * parameter. When live cadence enrollments exist, the sentence counts
 * active steps and names the most overdue prospect. Falls back to
 * hunt_items if cadence queue is empty.
 *
 * Canon: no exclamation points, no emojis, M-PACT hyphenated.
 */

import type { ApiAccount, HuntItem } from "@/pages/dashboards/live-data";
import type { CadenceQueueItem } from "@/hooks/useCadenceTodayQueue";

export interface StartHereResult {
  sentence: string;
  sectionId: string;
  linkText: string;
}

function approximateCpFromScore(score: number): number {
  if (score >= 90) return 9;
  if (score >= 80) return 8;
  if (score >= 70) return 7;
  if (score >= 60) return 6;
  if (score >= 50) return 5;
  if (score >= 40) return 4;
  if (score >= 30) return 3;
  if (score >= 20) return 2;
  return 1;
}

export function computeStartHereAe(accounts: ApiAccount[]): StartHereResult {
  const active = accounts.filter(
    (a) => a.stage === "Suspect" || a.stage === "Prospect" || a.stage === "Qualified Opp" ||
      a.stage === "Full Opportunity" || a.status === "active" || a.status === "open"
  );
  if (active.length === 0) return {
    sentence: "No accounts assigned yet — ask your manager to assign accounts in the Manager Hub.",
    sectionId: "path-a",
    linkText: "View deals",
  };
  const ranked = [...active].sort((a, b) => {
    const arrA = a.estArr ?? 0; const arrB = b.estArr ?? 0;
    const gapA = 100 - (a.score ?? 0); const gapB = 100 - (b.score ?? 0);
    return arrB * gapB - arrA * gapA;
  });
  const top = ranked[0];
  const currentCp = approximateCpFromScore(top.score ?? 0);
  const nextCp = Math.min(currentCp + 1, 10);
  return {
    sentence: `Your top move today: advance ${top.name} from Checkpoint ${currentCp} to Checkpoint ${nextCp} — they are closest to the forecast threshold.`,
    sectionId: "path-a",
    linkText: "Jump to deals to deepen",
  };
}

export function computeStartHereBdr(
  huntItems: HuntItem[],
  accounts: ApiAccount[],
  cadenceQueue?: CadenceQueueItem[]
): StartHereResult {
  // Primary: live cadence queue from cadence_enrollments
  const liveQueue = cadenceQueue ?? [];
  if (liveQueue.length > 0) {
    const N = liveQueue.length;
    const topItem = [...liveQueue].sort((a, b) => b.daysOverdue - a.daysOverdue)[0];
    const topCompany = topItem.company || topItem.prospectName || "a prospect";
    const daysOverdue = topItem.daysOverdue;
    const overdueClause = daysOverdue > 0
      ? ` — ${topCompany} is overdue by ${daysOverdue} day${daysOverdue !== 1 ? "s" : ""}`
      : ` — ${topCompany} is due today`;
    return {
      sentence: `Your top move today: complete ${N} cadence step${N !== 1 ? "s" : ""}${overdueClause}.`,
      sectionId: "cadence-queue",
      linkText: "Jump to cadence queue",
    };
  }

  // Fallback: hunt_items queue
  const queue = huntItems.filter((h) => h.status === "open" || h.status === "chasing");
  if (queue.length === 0) return {
    sentence: "Your cadence queue is clear for today — pull new prospects from the hunt list to enroll.",
    sectionId: "cadence-queue",
    linkText: "View hunt list",
  };

  const now = Date.now();
  const acctMap = new Map(accounts.map((a) => [a.id, a]));
  const overdue = queue.filter((h) => h.dueDate && new Date(h.dueDate).getTime() < now);
  const sorted = [...(overdue.length > 0 ? overdue : queue)].sort((a, b) => {
    const da = a.dueDate ? new Date(a.dueDate).getTime() : now;
    const db = b.dueDate ? new Date(b.dueDate).getTime() : now;
    return da - db;
  });
  const topHuntItem = sorted[0];
  const topAccount = acctMap.get(topHuntItem.accountId);
  const accountName = topAccount?.name ?? topHuntItem.label ?? `Account #${topHuntItem.accountId}`;
  const dueMs = topHuntItem.dueDate ? new Date(topHuntItem.dueDate).getTime() : now;
  const daysOverdue = Math.max(0, Math.floor((now - dueMs) / (1000 * 60 * 60 * 24)));
  const overdueClause = daysOverdue > 0
    ? ` — ${accountName} is overdue by ${daysOverdue} day${daysOverdue !== 1 ? "s" : ""}`
    : ` — ${accountName} is due today`;
  return {
    sentence: `Your top move today: complete ${queue.length} cadence step${queue.length !== 1 ? "s" : ""} in your queue${overdueClause}.`,
    sectionId: "cadence-queue",
    linkText: "Jump to cadence queue",
  };
}

export function computeStartHere(
  role: "ae" | "bdr",
  accounts: ApiAccount[],
  hunt: HuntItem[],
  cadenceQueue?: CadenceQueueItem[]
): StartHereResult {
  if (role === "ae") return computeStartHereAe(accounts);
  return computeStartHereBdr(hunt, accounts, cadenceQueue);
}
